import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, Copy, Home, PackageSearch } from "lucide-react";
import { Button } from "@/components/ui/button";
import WafidPayCard from "@/components/WafidPayCard";
import ShareEarnCard from "@/components/ShareEarnCard";
import api from "@/lib/api";
import { toast } from "sonner";

export default function ApplicationSuccess() {
  const location = useLocation();
  const navigate = useNavigate();
  const info = location.state;
  const [wafidExpiresAt] = useState(() => new Date(Date.now() + 24 * 3600 * 1000).toISOString());
  const [wafidRecovered, setWafidRecovered] = useState(false);
  const [share, setShare] = useState(null);
  const waLast4 = (info?.customer_whatsapp || localStorage.getItem("gu_customer_wa") || "").replace(/\D/g, "").slice(-4);

  useEffect(() => {
    if (!info?.application_number) return;
    api.get(`/applications/track/${info.application_number}`, { params: { wa_last4: waLast4 } }).then(({ data }) => {
      if (data.share_earn || data.reward_coupons?.length) setShare({ data: data.share_earn, coupons: data.reward_coupons });
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkWafidRecovered = async () => {
    try {
      const { data } = await api.get(`/applications/track/${info.application_number}`, { params: { wa_last4: waLast4 } });
      if (data.wafid_recovered) setWafidRecovered(true);
    } catch { /* ignore */ }
  };

  if (!info) {
    return (
      <div data-testid="application-success-page" className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center p-8 rounded-lg border border-[#E5E1DA] bg-white">
          <p data-testid="application-success-no-info" className="text-[var(--brand-secondary)] mb-6">
            No application details found. If you just submitted an application, please check your email/WhatsApp, or track it below.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button data-testid="application-success-back-home-btn" variant="outline" onClick={() => navigate("/")} className="border-[var(--brand-primary)] text-[var(--brand-primary)]">
              <Home size={16} className="mr-2" /> Back to Home
            </Button>
            <Button data-testid="application-success-track-btn" onClick={() => navigate("/track")} className="bg-[var(--brand-primary)] hover:bg-[#9E855A] text-white">
              <PackageSearch size={16} className="mr-2" /> Track Application
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="application-success-page" className="min-h-[60vh] flex items-center justify-center px-4 py-12">
      <div className={`${info.wafid_pay_link ? "max-w-3xl" : "max-w-md"} w-full text-center p-8 sm:p-10 rounded-lg border border-[#E5E1DA] bg-white`}>
        <CheckCircle2 size={52} className="mx-auto mb-4 text-green-600" />
        <h1 className="font-heading font-bold text-2xl text-[#1A1A1A] mb-2">Application Confirmed!</h1>
        {info.service_name && <p className="text-sm text-[var(--brand-secondary)] mb-4">{info.service_name}</p>}

        <div className="flex items-center justify-center gap-2 my-4">
          <span data-testid="application-success-number" className="font-mono text-lg font-semibold text-[var(--brand-primary)] bg-[color-mix(in_oklab,var(--brand-primary),transparent_90%)] px-4 py-2 rounded-md">
            {info.application_number}
          </span>
          <Button size="icon" variant="ghost" data-testid="application-success-copy-btn" onClick={() => { navigator.clipboard.writeText(info.application_number); toast.success("Copied!"); }}>
            <Copy size={16} />
          </Button>
        </div>
        <p className="text-sm text-[var(--brand-secondary)] mb-6">Save this number to track your application status anytime.</p>

        <p data-testid="application-success-confirmed-msg" className="text-green-700 font-medium mb-6">
          {info.wafid_pay_link
            ? "Payment successful!"
            : info.requires_payment
              ? "Payment successful! Our team will contact you shortly."
              : "No payment required. Our team will contact you shortly."}
        </p>

        {info.paid && info.wafid_pay_link && (
          <div className="mb-6 text-left">
            {wafidRecovered ? (
              <div data-testid="wafid-recovered-card" className="rounded-xl border border-green-300 bg-green-50 px-5 py-4 flex items-center gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-green-100">
                  <CheckCircle2 size={20} className="text-green-600" />
                </span>
                <div>
                  <p className="font-heading font-bold text-green-800 text-base">Slip recovered successfully!</p>
                  <p className="text-xs text-[var(--brand-secondary)]">Your WAFID payment is complete and your appointment slip has been recovered.</p>
                </div>
              </div>
            ) : (
              <WafidPayCard link={info.wafid_pay_link} expiresAt={wafidExpiresAt} onClose={checkWafidRecovered} />
            )}
          </div>
        )}

        {share && (
          <div className="mb-6 text-left" data-testid="success-share-earn-wrap">
            <ShareEarnCard data={share.data} coupons={share.coupons} />
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6 pt-6 border-t border-[#E5E1DA]">
          <Button data-testid="application-success-back-home-btn" variant="outline" onClick={() => navigate("/")} className="border-[var(--brand-primary)] text-[var(--brand-primary)]">
            <Home size={16} className="mr-2" /> Back to Home
          </Button>
          <Link to="/track">
            <Button data-testid="application-success-track-btn" className="bg-[var(--brand-primary)] hover:bg-[#9E855A] text-white w-full">
              <PackageSearch size={16} className="mr-2" /> Track Application
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
